import { call, put, takeEvery, delay, takeLeading } from 'redux-saga/effects'
import {
  types,
  ICreateGroupAction,
  IGetGroupByIdAction,
  IUpdateGroupAction,
  IDeleteGroupAction,
  IGroupDetailed,
} from '../types/group'
import { ICapability } from '../types/capability'
import { Modals } from '../types/modal'
import { push } from '../actions/router'
import * as actions from '../actions/group'
import * as groupsActions from '../actions/groups'
import * as groupsDetailedActions from '../actions/groupsDetailed'
import { modalOpen } from '../actions/modal'
import { invalidateGroupEditors } from '../actions/editorsPerGroup'
import * as services from '../services/groups'
import { trace, unwrapId } from '../services/common'
import { messages } from '../config/messages'

export function* createGroupSaga(action: ICreateGroupAction) {
  try {
    const groupData = yield call(services.createGroup, action.groupData)
    yield put(
      actions.createGroupSuccess(
        groupData,
        messages.createGroupCreateSuccess(groupData.name)
      )
    )
    yield put(groupsActions.getGroups())
    yield put(groupsDetailedActions.getGroupsDetailed())
    yield delay(500) // Show success message before redirect
    yield put(push('/settings/groups?tab=groups'))
  } catch (err) {
    trace(err)
    yield put(actions.createGroupFailure(err.message))
  }
}

export function* getGroupByIdSaga(action: IGetGroupByIdAction) {
  try {
    const groupData = yield call(services.getGroupById, action.groupId)
    yield put(actions.getGroupByIdSuccess(groupData))
  } catch (err) {
    trace(err)
    yield put(actions.getGroupByIdFailure(err.message))
  }
}

export function* updateGroupSaga(action: IUpdateGroupAction) {
  try {
    const groupData: IGroupDetailed = yield call(
      services.updateGroup,
      action.groupData
    )
    yield put(invalidateGroupEditors(action.groupData.name))
    const capabilitiesIds = (groupData.capabilities as ICapability[])
      .filter(({ requiresApproval }) => requiresApproval)
      .map(unwrapId)
    const isAddedRequireApprovalCapability = !!(
      action.initialCapabilitiesIds &&
      capabilitiesIds.some(
        (id) => !(action.initialCapabilitiesIds as string[]).includes(id)
      )
    )
    yield put(
      actions.updateGroupSuccess(
        groupData,
        messages.createGroupUpdateSuccess(
          action.groupData.name,
          isAddedRequireApprovalCapability
        )
      )
    )
    yield put(groupsActions.getGroups())
    yield put(groupsDetailedActions.getGroupsDetailed())
  } catch (err) {
    trace(err)
    yield put(actions.updateGroupFailure(err.message))
  }
}

export function* deleteGroupSaga(action: IDeleteGroupAction) {
  try {
    yield call(services.deleteGroup, action.groupId)
    yield put(groupsActions.getGroups())
    yield put(groupsDetailedActions.getGroupsDetailed())
    yield put(
      actions.deleteGroupSuccess(
        messages.createGroupDeleteSuccess(action.groupId)
      )
    )
    yield put(push('/settings/groups?tab=groups'))
  } catch (err) {
    trace(err)
    if (err.code === 404) {
      yield put(push('/settings/groups?tab=groups'))
    } else {
      yield put(modalOpen(Modals.deleteStatusModal))
    }
    yield put(actions.deleteGroupFailure(err.message))
  }
}

export function* groupWatcher() {
  yield takeLeading(types.CREATE_GROUP__STARTED, createGroupSaga)
  yield takeEvery(types.GET_GROUP_BY_ID__STARTED, getGroupByIdSaga)
  yield takeLeading(types.UPDATE_GROUP__STARTED, updateGroupSaga)
  yield takeLeading(types.DELETE_GROUP__STARTED, deleteGroupSaga)
}
